import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Table,
  Tabs,
  Button,
  Tag,
  message,
  Spin,
  Select,
  DatePicker,
  Tooltip,
  Modal,
  Input,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchApplicationRequests,
  updateApplicationRequest,
} from "../../../../../Store/Slices/Admin/ApplicationRequests/applicationRequestsThunk";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
} from "@ant-design/icons";
import ApplicationDetails from "../../../ApplicationRequests/components/ApplicationDetails";
import Sidebar from "../../../../../Components/Common/Sidebar";
import { useTranslation } from "react-i18next";
import ProtectedAction from "../../../../../Routes/ProtectedRoutes/ProtectedAction";
import { PERMISSIONS } from "../../../../../config/permission";

const { TabPane } = Tabs;
const { Option } = Select;
const { Search } = Input;

const LeaveRequests = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("admLeaveApplications");
  const { applicationRequestsData, loading, totalRecords } = useSelector(
    (state) => state.admin.applicationRequests
  );
  const { userDetails } = useSelector((store) => store.common.user);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [currentApplicationId, setCurrentApplicationId] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const refetch = () => {
    dispatch(
      fetchApplicationRequests({
        status: statusFilter,
        applicantType: "staff",
        isLeaveApplication: true,
        search: searchText,
        page,
        limit,
      })
    );
  };

  useEffect(() => {
    refetch();
  }, [dispatch, statusFilter, selectedDate, searchText, page, limit]);

  const handleApprove = (id) => {
    let remarks;
    Modal.confirm({
      title: "Are you sure you want to approve this leave application?",
      content: (
        <Input.TextArea
          placeholder="Enter remarks (optional)"
          onChange={(e) => (remarks = e.target.value)}
          rows={3}
        />
      ),
      okButtonProps: {
        className: "btn-gradient",
      },
      onOk: async () => {
        await dispatch(
          updateApplicationRequest({
            applicationId: id,
            status: "approved",
            staffId: userDetails?.userId,
            remarks
          })
        ).then((res) => {
          if (res?.payload?.success) {
            message.success(res?.payload?.message);
            refetch();
          } else {
            message.error(res?.payload?.response?.data?.message);
          }
        });
      },
    });
  };

  const handleReject = (id) => {
    let remarks;
    Modal.confirm({
      title: "Are you sure you want to reject this leave application?",
      content: (
        <Input.TextArea
          placeholder="Enter remarks (optional)"
          onChange={(e) => (remarks = e.target.value)}
          rows={3}
        />
      ),
      okButtonProps: { danger: true },
      onOk: async () => {
        await dispatch(
          updateApplicationRequest({
            applicationId: id,
            status: "rejected",
            staffId: userDetails?.userId,
            remarks
          })
        ).then((res) => {
          if (res?.payload?.success) {
            message.success(res?.payload?.message);
            refetch();
          } else {
            message.error(res?.payload?.response?.data?.message);
          }
        });
      },
    });
  };

  const handleChangeStatus = (id) => {
    const app = applicationRequestsData.find((item) => item._id === id);
    setNewStatus(app?.applicationStatus || "");
    setCurrentApplicationId(id);
    setStatusModalVisible(true);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      message.warning("Please select a status.");
      return;
    }
    await dispatch(
      updateApplicationRequest({
        applicationId: currentApplicationId,
        status: newStatus,
        staffId: userDetails?.userId,
      })
    ).then((res) => {
      if (res?.payload?.success) {
        message.success(res?.payload?.message);
        setStatusModalVisible(false);
        setNewStatus("");
        refetch();
      } else {
        message.error(res?.payload?.response?.data?.message);
      }
    });
  };

  const hasBothDates = applicationRequestsData?.some(
    (item) => item.fromDate && item.toDate
  );

  const hasOnlyFromDate = applicationRequestsData?.some(
    (item) => item.fromDate && !item.toDate
  );

  const columns = [
    {
      title: "Staff Name",
      dataIndex: ["applicant", "firstName"],
      render: (_, record) =>
        record?.applicant?.firstName + " " + record?.applicant?.lastName || "-",
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (subject) => subject || "-",
    },
    ...(hasBothDates
      ? [
        {
          title: "From Date",
          dataIndex: "fromDate",
          key: "fromDate",
          render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "-"),
        },
        {
          title: "To Date",
          dataIndex: "toDate",
          key: "toDate",
          render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "-"),
        },
      ]
      : hasOnlyFromDate
        ? [
          {
            title: "Date",
            dataIndex: "fromDate",
            key: "fromDate",
            render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "-"),
          },
        ]
        : []),
    {
      title: "Status",
      dataIndex: "applicationStatus",
      render: (status) => {
        let color = "default";
        if (status === "approved") color = "green";
        if (status === "rejected") color = "red";
        if (status === "pending") color = "orange";
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div className="flex gap-2">
          <Tooltip title="Details">
            <Button
              size="small"
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedApplication(record);
                setDetailsDrawerOpen(true);
              }}
            />
          </Tooltip>
          <ProtectedAction requiredPermission={PERMISSIONS.MANAGE_LEAVE_APPROVAL}>
            {record.applicationStatus === "pending" && (
              <>
                <Tooltip title="Approve">
                  <Button
                    size="small"
                    type="text"
                    icon={<CheckOutlined />}
                    onClick={() => handleApprove(record._id)}
                  />
                </Tooltip>
                <Tooltip title="Reject">
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => handleReject(record._id)}
                  />
                </Tooltip>
              </>
            )}
            {(record.applicationStatus === "approved" ||
              record.applicationStatus === "rejected") && (
                <Tooltip title="Edit">
                  <Button
                    size="small"
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleChangeStatus(record._id)}
                  />
                </Tooltip>
              )}
          </ProtectedAction>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 bg-white rounded-lg space-y-4">
      <h2 className="text-md font-semibold text-gradient">
        {/* {t("Leave Application List")} */}
      </h2>

      <Tabs
        defaultActiveKey=""
        onChange={(key) => {
          setStatusFilter(key);
          setPage(1);
        }}
      >
        <TabPane tab="All" key="" />
        <TabPane tab="Pending" key="pending" />
        <TabPane tab="Approved" key="approved" />
        <TabPane tab="Rejected" key="rejected" />
      </Tabs>
      {loading ? (
        <div className="flex justify-center items-center h-60">
          <Spin size="large" />
        </div>
      ) : (
        <Table
          dataSource={applicationRequestsData}
          columns={columns}
          rowKey="_id"
          pagination={{
            current: page,
            pageSize: limit,
            pageSizeOptions: ["5", "10", "20", "50", "100", "500"],
            total: totalRecords,
            showSizeChanger: true,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setLimit(newPageSize);
            },
          }}
        />
      )}

      <Sidebar
        isOpen={detailsDrawerOpen}
        onClose={() => {
          setDetailsDrawerOpen(false);
          setSelectedApplication(null);
        }}
        title="Application Details"
        width={620}
      >
        <ApplicationDetails testData={selectedApplication} />
      </Sidebar>

      <Modal
        title="Change Application Status"
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          setNewStatus("");
        }}
        onOk={handleStatusUpdate}
        okText="Update"
      >
        <Select
          value={newStatus}
          onChange={(value) => setNewStatus(value)}
          placeholder="Select status"
          style={{ width: "100%" }}
        >
          <Option value="pending">Pending</Option>
          <Option value="approved">Approve</Option>
          <Option value="rejected">Reject</Option>
        </Select>
      </Modal>
    </div>
  );
};

export default LeaveRequests;
